/* HARUM (HAR-230): Personal Shopper page.

   Replaces customOrderVercel.js, which was jQuery-bound to the ported Expanse
   markup. The estimate rules below are a faithful port of that file so the
   number a shopper sees — and the number CS sees in the confirmation email —
   does not move: KRW converted at harum's own rate, USD$5 service fee, USD$5
   per second-hand item, and a weight-banded international shipping fee.

   Two behaviours that were broken before are fixed here on purpose:
   - the old AJAX call had an empty `error` handler, so a failed submit left
     the shopper staring at "the form is submitting..." forever;
   - no `<form>` element is used at all, which sidesteps the Shopify
     `captcha-bootstrap` script that force-submits contact forms (see HAR-148).
*/
(function () {
  'use strict';

  var IS_LOCAL = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  var API_BASE = IS_LOCAL ? 'http://localhost:3000' : 'https://supply.harum.io';
  var PREVIEW_URL = API_BASE + '/api/public/product-preview';
  var SUBMIT_URL = API_BASE + '/api/custom-orders';
  var RATE_URL = 'https://searchkorea.harum.io/api/harum/rate';

  /* harum's own KRW-per-USD rate (includes PayPal fees). The fallback matches
     the value customOrderVercel.js shipped with. */
  var DEFAULT_RATE = 1230;

  /* Ported verbatim from customOrderVercel.js: express couriers cost more per
     shipment, and every extra 0.5 KG over the first kilo adds USD$5. */
  var SHIPPING_BASE = { 'dhl/fedex': 34, dhl: 34, fedex: 34 };
  var SHIPPING_BASE_ECONOMY = 20;
  var OVERWEIGHT_PER_KG = 10;

  /* Which couriers each destination may pick, first entry preselected. */
  var METHODS_BY_COUNTRY = {
    Malaysia: ['skynet'],
    Singapore: ['cj'],
    Philippines: ['ninjavan', 'dhl/fedex'],
    Taiwan: ['chunghwa', 'dhl/fedex'],
    Japan: ['sagawa', 'dhl/fedex'],
    'United States': ['dhl/fedex', 'usps']
  };
  var DEFAULT_METHODS = ['dhl/fedex', 'airparcel'];

  /* Destinations that levy import duty on the receiver. The old form disabled
     submit until the shopper accepted the charge; same rule, nicer control. */
  var TAX_COUNTRIES = [
    'France', 'Ireland', 'Greece', 'Philippines', 'Canada', 'Brazil',
    'Tunisia', 'Peru', 'Mexico', 'United Kingdom'
  ];

  function money(usd) {
    return 'USD$' + usd.toFixed(2);
  }

  function won(krw) {
    return '₩' + Math.round(krw).toLocaleString('en-US');
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  /* Links added manually never pass through the preview API, so they are the
     one path where a `javascript:` URL could reach an href. */
  function safeHref(value) {
    return /^https?:\/\//i.test(value || '') ? value : '';
  }

  /* A hand-typed link usually arrives without its scheme; store it with one so
     the CS admin, the confirmation email and the item list all get a URL they
     can actually follow. */
  function normalizeUrl(value) {
    var url = String(value == null ? '' : value).trim();
    if (!url) return '';
    return /^https?:\/\//i.test(url) ? url : 'https://' + url;
  }

  function init(root) {
    var $ = function (name) {
      return root.querySelector('[data-ps-' + name + ']');
    };

    var state = {
      items: [],
      rate: DEFAULT_RATE,
      editingId: null,
      nextId: 1,
      draft: null,
      /* what was in the dialog when it was closed without saving (HAR-239) */
      stash: null
    };

    /* Set while saveDialog() closes the dialog, so a saved item is not also
       kept as an unfinished draft. */
    var skipStash = false;

    var serviceFee = parseFloat(root.dataset.serviceFee) || 0;
    var secondHandFee = parseFloat(root.dataset.secondHandFee) || 0;

    var els = {
      urlInput: $('url-input'),
      addBtn: $('add'),
      addLabel: $('add-label'),
      pasteError: $('paste-error'),
      manualBtn: $('manual'),
      resumeBtn: $('resume'),
      itemList: $('item-list'),
      empty: $('empty'),
      details: $('details'),
      name: $('name'),
      email: $('email'),
      country: $('country'),
      method: $('method'),
      weight: $('weight'),
      tax: $('tax'),
      taxCopy: $('tax-copy'),
      taxAgree: $('tax-agree'),
      submit: $('submit'),
      submitLabel: $('submit-label'),
      submitError: $('submit-error'),
      sumItems: $('sum-items'),
      sumSecondHandRow: $('sum-secondhand-row'),
      sumSecondHand: $('sum-secondhand'),
      sumShipping: $('sum-shipping'),
      sumService: $('sum-service'),
      sumTotal: $('sum-total'),
      dialog: $('dialog'),
      dialogClose: $('dialog-close'),
      dialogUrl: $('dialog-url'),
      dialogPreview: $('dialog-preview'),
      dialogImage: $('dialog-image'),
      dialogName: $('dialog-name'),
      dialogQty: $('dialog-qty'),
      dialogPrice: $('dialog-price'),
      dialogPriceNote: $('dialog-price-note'),
      dialogComment: $('dialog-comment'),
      dialogSecondHand: $('dialog-secondhand'),
      dialogSubtotal: $('dialog-subtotal'),
      dialogSave: $('dialog-save'),
      qtyUp: $('qty-up'),
      qtyDown: $('qty-down'),
      review: $('review'),
      reviewBody: $('review-body'),
      reviewClose: $('review-close'),
      reviewBack: $('review-back'),
      reviewConfirm: $('review-confirm'),
      done: $('done'),
      doneClose: $('done-close'),
      /* HAR-260: the "What you pay for" popup. Absent when the section has no
         cost rows, so every use of it is guarded. */
      cost: $('cost'),
      costClose: $('cost-close')
    };

    /* ---------------------------------------------------------------- rate */

    /* The sidebar can carry a "Conversion rate: {rate} KRW = $1.00 USD" line;
       fill the token with whatever rate the estimate is actually using so the
       two can never disagree. */
    function renderRateNotes() {
      root.querySelectorAll('[data-ps-rate-note]').forEach(function (node) {
        if (!node.dataset.template) node.dataset.template = node.textContent;
        node.textContent = node.dataset.template.replace(
          /\{rate\}/g,
          Math.round(state.rate).toLocaleString('en-US')
        );
      });
    }

    fetch(RATE_URL)
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        if (data && data.rate > 0) {
          state.rate = data.rate;
          renderSummary();
        }
      })
      .catch(function () {
        /* keep DEFAULT_RATE — the estimate is explicitly an estimate */
      })
      .then(renderRateNotes);

    /* ------------------------------------------------------------- estimate */

    /* Sub-USD1 lines were floored to USD$1 in the old form; keep that so a
       ₩500 sticker still carries its share of the handling cost. */
    function itemUsd(item) {
      var totalWon = item.price * item.quantity;
      if (totalWon < state.rate) return 1;
      return Math.round((totalWon / state.rate) * 100) / 100;
    }

    function billableWeight() {
      var entered = parseFloat(els.weight.value);
      if (!isFinite(entered) || entered <= 0) return 1;
      var rounded = Math.ceil(entered * 2) / 2;
      return rounded < 1 ? 1 : rounded;
    }

    function shippingUsd() {
      var method = els.method.value;
      var weight = billableWeight();
      /* AirParcel is capped at 2 KG; heavier parcels fall back to express. */
      if (method === 'airparcel' && weight > 2) method = 'dhl/fedex';
      var base = SHIPPING_BASE[method] || SHIPPING_BASE_ECONOMY;
      return base + Math.max(0, weight - 1) * OVERWEIGHT_PER_KG;
    }

    function estimate() {
      var items = 0;
      var secondHand = 0;
      state.items.forEach(function (item) {
        items += itemUsd(item);
        if (item.secondHand) secondHand += secondHandFee;
      });
      var shipping = state.items.length ? shippingUsd() : 0;
      var service = state.items.length ? serviceFee : 0;
      return {
        items: items,
        secondHand: secondHand,
        secondHandCount: state.items.filter(function (i) {
          return i.secondHand;
        }).length,
        shipping: shipping,
        service: service,
        total: items + secondHand + shipping + service
      };
    }

    /* -------------------------------------------------------------- render */

    function renderSummary() {
      var e = estimate();
      els.sumItems.textContent = money(e.items);
      els.sumShipping.textContent = money(e.shipping);
      els.sumService.textContent = money(e.service);
      els.sumTotal.textContent = money(e.total);
      els.sumSecondHandRow.hidden = e.secondHandCount === 0;
      els.sumSecondHand.textContent =
        money(e.secondHand) + ' (' + e.secondHandCount + ' item' +
        (e.secondHandCount === 1 ? '' : 's') + ')';
    }

    function renderItems() {
      els.itemList.innerHTML = '';
      state.items.forEach(function (item, index) {
        var li = document.createElement('li');
        li.className = 'harum-ps__item';
        li.innerHTML =
          '<div class="harum-ps__item-thumb">' +
          (item.image
            ? '<img src="' + escapeHtml(item.image) +
              '" alt="" loading="lazy" width="80" height="80">'
            : '<span class="harum-ps__item-thumb-empty" aria-hidden="true"></span>') +
          '</div>' +
          '<div class="harum-ps__item-body">' +
          '<p class="harum-ps__item-name">' + escapeHtml(item.name) + '</p>' +
          '<a class="harum-ps__item-url" href="' + escapeHtml(safeHref(item.url)) +
          '" target="_blank" rel="noopener nofollow">' + escapeHtml(item.url) + '</a>' +
          (item.comments
            ? '<p class="harum-ps__item-note">' + escapeHtml(item.comments) + '</p>'
            : '') +
          (item.secondHand
            ? '<p class="harum-ps__item-tag">Second-hand</p>'
            : '') +
          '</div>' +
          '<div class="harum-ps__item-figures">' +
          '<p class="harum-ps__item-price">' + won(item.price * item.quantity) + '</p>' +
          '<p class="harum-ps__item-qty">Qty ' + item.quantity + '</p>' +
          '<div class="harum-ps__item-actions">' +
          '<button type="button" data-edit="' + index + '">Edit</button>' +
          '<button type="button" data-remove="' + index + '">Remove</button>' +
          '</div></div>';
        els.itemList.appendChild(li);
      });

      els.empty.hidden = state.items.length > 0;
      els.details.hidden = state.items.length === 0;
      renderSummary();
    }

    /* -------------------------------------------------------------- dialog */

    /* <dialog>.showModal() gives us the focus trap, Escape handling and inert
       background for free; we only add the body scroll lock on top. */
    function openOverlay(dialog) {
      dialog.showModal();
      document.body.classList.add('harum-ps-locked');
    }

    function closeOverlay(dialog) {
      dialog.close();
    }

    var overlays = [els.dialog, els.review, els.done];
    /* The item dialog deliberately has no click-outside-to-close: a stray click
       used to wipe everything typed into it (HAR-239). The others hold no
       unsaved input, so there the backdrop still closes them. */
    var backdropCloses = [els.review, els.done];
    if (els.cost) {
      overlays.push(els.cost);
      backdropCloses.push(els.cost);
    }

    overlays.forEach(function (dialog) {
      dialog.addEventListener('close', function () {
        if (!root.querySelector('dialog[open]')) {
          document.body.classList.remove('harum-ps-locked');
        }
      });
    });

    /* Clicking the backdrop lands on the dialog element itself. */
    backdropCloses.forEach(function (dialog) {
      dialog.addEventListener('click', function (event) {
        if (event.target !== dialog) return;
        var box = dialog.getBoundingClientRect();
        var inside =
          event.clientX >= box.left && event.clientX <= box.right &&
          event.clientY >= box.top && event.clientY <= box.bottom;
        if (!inside) dialog.close();
      });
    });

    /* ------------------------------------------------- name translation */

    /* Marketplace titles come back in Korean, which most of our shoppers cannot
       read. Chrome/Edge 138+ expose an on-device translator, so where it exists
       we show the item name in English. Everywhere else — Safari, Chrome on
       Android, older builds — nothing runs and the Korean name stays, exactly as
       before. Progressive enhancement only: the name we SUBMIT is always the
       Korean original (see saveDialog / itemName), because that is the string CS
       searches with when they place the buy. */

    /* Hangul Jamo, Compatibility Jamo, Jamo Extended-A/B, Syllables and
       Halfwidth forms — escaped so an encoding mangle cannot silently
       narrow the ranges. */
    var HANGUL =
      /[\u1100-\u11FF\u3130-\u318F\uA960-\uA97F\uAC00-\uD7A3\uD7B0-\uD7FF\uFFA0-\uFFDC]/;
    var translatorPromise = null;

    function getNameTranslator() {
      /* Feature-detect the methods, not just the global: a foreign script can
         claim window.Translator, and a synchronous throw here would take the
         whole add flow down with it. */
      if (
        !window.Translator ||
        typeof window.Translator.availability !== 'function' ||
        typeof window.Translator.create !== 'function'
      ) {
        return Promise.resolve(null);
      }
      if (!translatorPromise) {
        var pair = { sourceLanguage: 'ko', targetLanguage: 'en' };
        translatorPromise = Promise.resolve()
          .then(function () {
            return window.Translator.availability(pair);
          })
          .then(function (availability) {
            if (availability === 'unavailable') return null;
            /* First call downloads the ko->en pack; it needs the user gesture
               that opened this dialog, so never call it off a timer. */
            return window.Translator.create(pair);
          })
          .catch(function () {
            translatorPromise = null; // let the next dialog try again
            return null;
          });
      }
      return translatorPromise;
    }

    function showNameInEnglish() {
      /* Translate only a fresh, marketplace-sourced name. Never while
         editing — the name on the list is the shopper's confirmed wording,
         machine or human — and never a draft that already carries a Korean
         original: re-translating a partially-translated name would replace
         the true marketplace title CS searches with. */
      if (state.editingId !== null) return;
      if (!state.draft || state.draft.nameOriginal) return;

      var korean = els.dialogName.value.trim();
      if (!korean || !HANGUL.test(korean)) return;

      var draft = state.draft;

      /* Returned so it can be awaited in tests; openDialog deliberately does
         not wait on it — the dialog must open at once either way. */
      return getNameTranslator()
        .then(function (translator) {
          if (!translator) return null;
          return translator.translate(korean).catch(function () {
            /* Chrome may purge the on-device model mid-session; the only
               documented recovery is a fresh create(), so drop the dead
               instance and let the next dialog retry. */
            translatorPromise = null;
            return null;
          });
        })
        .then(function (english) {
          english = (english || '').trim();
          if (!english || english === korean) return;
          /* The pack download can take seconds: bail if the dialog has since
             been closed or reopened for another item, or the shopper has
             typed over the name. The draft is compared by identity — its
             .name can legally differ from the input (the browser strips
             CR/LF from scraped titles on assignment). */
          if (!els.dialog.open) return;
          if (state.draft !== draft) return;
          if (els.dialogName.value.trim() !== korean) return;

          els.dialogName.value = english;
          /* saveDialog compares against draft.name to tell an untouched field
             from an edited one, so move it in step with what is displayed. */
          draft.name = english;
          draft.nameOriginal = korean;
        })
        .catch(function () {
          // Translation failed: leave the Korean name alone.
        });
    }

    /* ------------------------------------------------- unfinished item draft */

    function setStash(draft) {
      state.stash = draft || null;
      els.resumeBtn.hidden = !state.stash;
    }

    /* Whatever is in the dialog right now, in the shape openDialog() reads. */
    function readDialog() {
      var price = parseFloat(els.dialogPrice.value);
      var name = els.dialogName.value.trim();
      return {
        url: els.dialogUrl.value.trim(),
        name: name,
        /* Same rule as saveDialog: a stashed draft keeps its Korean original
           only while the shown name is the untouched translation, so a
           resumed draft still submits the marketplace title. */
        nameOriginal:
          state.draft && name === state.draft.name
            ? state.draft.nameOriginal || null
            : null,
        price: isFinite(price) ? price : null,
        quantity: parseInt(els.dialogQty.value, 10) || 1,
        comments: els.dialogComment.value.trim(),
        secondHand: els.dialogSecondHand.checked,
        image: state.draft ? state.draft.image : null,
        domesticShipping: state.draft ? state.draft.domesticShipping : null,
        priceNote: state.draft ? state.draft.priceNote : ''
      };
    }

    function isWorthKeeping(draft) {
      return !!(draft.url || draft.name || draft.price !== null || draft.comments);
    }

    /* Escape and the close button are deliberate, so they are allowed to close
       the dialog — but what was typed is kept and offered back instead of being
       thrown away. Cancelling an edit is not a draft: that item is still in the
       list, untouched. */
    els.dialog.addEventListener('close', function () {
      if (skipStash) {
        skipStash = false;
        return;
      }
      if (state.editingId !== null) return;
      var draft = readDialog();
      /* Only overwrite the stash with something worth keeping — closing an
         empty dialog must not throw away a draft parked earlier. */
      if (isWorthKeeping(draft)) setStash(draft);
    });

    function renderDialogSubtotal() {
      var price = parseFloat(els.dialogPrice.value) || 0;
      var qty = parseInt(els.dialogQty.value, 10) || 0;
      els.dialogSubtotal.textContent = won(price * qty);
    }

    function openDialog(draft, editingIndex) {
      state.draft = draft;
      state.editingId = typeof editingIndex === 'number' ? editingIndex : null;

      els.dialogUrl.value = draft.url || '';
      els.dialogName.value = draft.name || '';
      els.dialogQty.value = draft.quantity || 1;
      els.dialogPrice.value = draft.price === null ? '' : draft.price;
      els.dialogComment.value = draft.comments || '';
      els.dialogSecondHand.checked = !!draft.secondHand;

      /* Drop the previous item's photo as well as hiding its frame — a stale
         `src` was what made a manually added item show the last product read
         from a link (HAR-238). */
      els.dialogPreview.hidden = !draft.image;
      if (draft.image) {
        els.dialogImage.src = draft.image;
      } else {
        els.dialogImage.removeAttribute('src');
      }

      /* clear as well as hide, so the previous item's note cannot linger in
         the DOM for assistive tech */
      els.dialogPriceNote.textContent = draft.priceNote || '';
      els.dialogPriceNote.hidden = !draft.priceNote;

      els.dialogSave.textContent =
        state.editingId === null ? 'Add to list' : 'Save changes';
      renderDialogSubtotal();
      openOverlay(els.dialog);
      showNameInEnglish();
    }

    function saveDialog() {
      var name = els.dialogName.value.trim();
      var price = parseFloat(els.dialogPrice.value);
      var qty = parseInt(els.dialogQty.value, 10);

      if (!name) {
        els.dialogName.focus();
        els.dialogName.reportValidity();
        return;
      }
      if (!isFinite(price) || price <= 0) {
        els.dialogPrice.focus();
        els.dialogPrice.setCustomValidity('Enter the price in Korean Won.');
        els.dialogPrice.reportValidity();
        els.dialogPrice.setCustomValidity('');
        return;
      }
      if (!isFinite(qty) || qty < 1) qty = 1;

      var item = {
        url: normalizeUrl(els.dialogUrl.value),
        name: name,
        /* Shopper typed over the translated name: their wording is the order,
           so drop the Korean original rather than submitting it instead. */
        nameOriginal:
          name === state.draft.name ? state.draft.nameOriginal || null : null,
        price: price,
        quantity: qty,
        comments: els.dialogComment.value.trim(),
        secondHand: els.dialogSecondHand.checked,
        image: state.draft.image || null,
        domesticShipping: state.draft.domesticShipping
      };

      if (state.editingId === null) {
        state.items.push(item);
      } else {
        state.items[state.editingId] = item;
      }

      skipStash = true;
      closeOverlay(els.dialog);
      setStash(null);
      renderItems();
      els.urlInput.value = '';
    }

    /* ------------------------------------------------------------- preview */

    function setBusy(button, label, busy, busyText) {
      button.disabled = busy;
      button.classList.toggle('is-busy', busy);
      if (!label) return;
      if (busy) {
        label.dataset.original = label.textContent;
        label.textContent = busyText;
      } else if (label.dataset.original) {
        label.textContent = label.dataset.original;
      }
    }

    function showPasteError(message) {
      els.pasteError.textContent = message;
      els.pasteError.hidden = !message;
    }

    function draftFromPreview(preview, url) {
      var priceNote = '';
      var price = null;

      if (typeof preview.priceKrw === 'number') {
        price = preview.priceKrw;
        if (preview.conversionRate) {
          priceNote =
            'Converted from ' + preview.price.toLocaleString('en-US') + ' ' +
            preview.currency + ' at 1 ' + preview.currency + ' ≈ ' +
            preview.conversionRate.toFixed(2) + ' KRW. Please double-check.';
        }
      } else if (typeof preview.price === 'number') {
        /* Price found but we could not express it in KRW — showing it as-is
           would silently be treated as Won, so make the shopper confirm. */
        priceNote =
          'We found ' + preview.price.toLocaleString('en-US') +
          (preview.currency ? ' ' + preview.currency : '') +
          ' on the page but could not convert it. Please enter the KRW amount.';
      } else {
        priceNote = 'We could not read a price — please enter it in KRW.';
      }

      return {
        url: preview.url || url,
        name: preview.title || '',
        /* Set only if the browser translates the name for display (see
           showNameInEnglish); holds the Korean we still submit. */
        nameOriginal: null,
        price: price,
        quantity: 1,
        comments: '',
        secondHand: !!preview.secondHand,
        image: preview.image || null,
        domesticShipping:
          typeof preview.domesticShipping === 'number'
            ? preview.domesticShipping
            : null,
        priceNote: priceNote
      };
    }

    function addFromUrl() {
      var url = els.urlInput.value.trim();
      showPasteError('');

      if (!url) {
        els.urlInput.focus();
        return;
      }
      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
      }

      setBusy(els.addBtn, els.addLabel, true, 'Reading…');

      fetch(PREVIEW_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok || !result.data || result.data.ok !== true) {
            /* A shop we cannot read is not a dead end: open the dialog empty
               so the shopper can fill it in the way the old form required. */
            showPasteError(
              (result.data && result.data.message) ||
                'We could not read that link.' +
                ' Fill the details in below and we will take it from there.'
            );
            openDialog(draftFromPreview({ url: url }, url));
            return;
          }
          openDialog(draftFromPreview(result.data, url));
        })
        .catch(function () {
          showPasteError(
            'We could not reach our product lookup service.' +
              ' Please fill the details in manually.'
          );
          openDialog(draftFromPreview({ url: url }, url));
        })
        .then(function () {
          setBusy(els.addBtn, els.addLabel, false);
        });
    }

    /* -------------------------------------------------------- country rules */

    function applyCountryRules() {
      var country = els.country.value;
      var allowed = METHODS_BY_COUNTRY[country] || DEFAULT_METHODS;

      Array.prototype.forEach.call(els.method.options, function (option) {
        var permitted = allowed.indexOf(option.value) !== -1;
        option.hidden = !permitted;
        option.disabled = !permitted;
      });

      /* Always snap to the destination's preferred courier, the way the old
         form did — it is the one we actually quote for, and the two differ by
         USD$14 on the base rate. */
      els.method.value = allowed[0];

      var taxed = TAX_COUNTRIES.indexOf(country) !== -1;
      els.tax.hidden = !taxed;
      if (taxed) {
        els.taxCopy.textContent =
          'Parcels to ' + country + ' are subject to customs tax, payable to' +
          ' the courier when the parcel arrives. We cannot refund a parcel that' +
          ' is refused after it ships.';
      } else {
        els.taxAgree.checked = false;
      }
      renderSummary();
    }

    /* -------------------------------------------------------------- submit */

    function validateDetails() {
      if (!state.items.length) return 'Add at least one item first.';
      if (!els.name.value.trim()) {
        els.name.focus();
        return 'Please enter your name.';
      }
      if (!isValidEmail(els.email.value.trim())) {
        els.email.focus();
        return 'Please enter a valid email address.';
      }
      if (!els.country.value) {
        els.country.focus();
        return 'Please choose your shipping country.';
      }
      if (!els.tax.hidden && !els.taxAgree.checked) {
        els.taxAgree.focus();
        return 'Please confirm you agree to pay customs tax.';
      }
      return null;
    }

    function openReview() {
      var problem = validateDetails();
      els.submitError.hidden = !problem;
      if (problem) {
        els.submitError.textContent = problem;
        return;
      }

      var e = estimate();
      var rows = state.items
        .map(function (item) {
          return (
            '<tr><td>' + escapeHtml(item.name) +
            (item.comments
              ? '<br><span class="harum-ps__review-note">' +
                escapeHtml(item.comments) + '</span>'
              : '') +
            '</td><td>' + item.quantity + '</td><td>' +
            won(item.price * item.quantity) + '</td><td>' +
            money(itemUsd(item)) + '</td></tr>'
          );
        })
        .join('');

      els.reviewBody.innerHTML =
        '<table class="harum-ps__review-table"><thead><tr>' +
        '<th>Item</th><th>Qty</th><th>Price</th><th>Est. USD</th>' +
        '</tr></thead><tbody>' + rows + '</tbody></table>' +
        '<dl class="harum-ps__review-totals">' +
        '<div><dt>Items</dt><dd>' + money(e.items) + '</dd></div>' +
        (e.secondHandCount
          ? '<div><dt>Second-hand handling (' + e.secondHandCount +
            ')</dt><dd>' + money(e.secondHand) + '</dd></div>'
          : '') +
        '<div><dt>Shipping (' + billableWeight() + ' KG, ' +
        escapeHtml(els.method.value.toUpperCase()) + ')</dt><dd>' +
        money(e.shipping) + '</dd></div>' +
        '<div><dt>Service fee</dt><dd>' + money(e.service) + '</dd></div>' +
        '<div class="harum-ps__review-total"><dt>Estimated total</dt><dd>' +
        money(e.total) + '</dd></div></dl>' +
        '<p class="harum-ps__review-foot">Sent to ' +
        escapeHtml(els.email.value.trim()) + '</p>';

      openOverlay(els.review);
    }

    function submitOrder() {
      var e = estimate();
      var totalText = money(e.total) + ' (Estimation only)';

      var payload = {
        name: els.name.value.trim(),
        email: els.email.value.trim(),
        country: els.country.value,
        shipMethod: els.method.value,
        estimateWeight: billableWeight(),
        bunjangEstimate:
          money(e.secondHand) + ' for ' + e.secondHandCount +
          ' product(s) (Estimation only)',
        totalEstimate: totalText,
        totalEstimateShown: totalText,
        taxAgree: els.tax.hidden ? 'N/A' : els.taxAgree.checked ? 'Yes' : 'No',
        products: state.items.map(function (item) {
          return {
            itemUrl: item.url,
            /* Korean original when we translated the name for display, so the
               payload stays exactly what CS and the emails expected before. */
            itemName: item.nameOriginal || item.name,
            price: item.price,
            quantity: item.quantity,
            comments: item.comments,
            secondHand: item.secondHand,
            domesticShipping: item.domesticShipping
          };
        })
      };

      var confirmLabel = els.reviewConfirm.querySelector('.btn__text');
      setBusy(els.reviewConfirm, confirmLabel, true, 'Sending…');

      fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('submit failed: ' + res.status);
          closeOverlay(els.review);
          openOverlay(els.done);
          state.items = [];
          setStash(null);
          renderItems();
        })
        .catch(function () {
          closeOverlay(els.review);
          els.submitError.hidden = false;
          els.submitError.textContent =
            'We could not send your request. Please try again, or email' +
            ' support@harum.io and we will take it from there.';
        })
        .then(function () {
          setBusy(els.reviewConfirm, confirmLabel, false);
        });
    }

    /* -------------------------------------------------------------- events */

    /* HAR-249: one box takes both a product link and a keyword. A link starts
       the paste flow as before; anything else opens the external search
       (Bunjang / Naver via searchkorea - see harum-external-search.js) in a
       new tab, so a half-built request here is never lost. The button label
       follows what is typed so the shopper can see which will happen. */
    var addLabelText = els.addLabel.textContent;
    var searchLabelText = els.addBtn.getAttribute('data-ps-search-label') || 'Search';
    var searchUrl = root.dataset.searchUrl || '/search';

    function looksLikeUrl(value) {
      var text = (value || '').trim();
      if (!text || /\s/.test(text)) return false;
      if (/^https?:\/\//i.test(text)) return true;
      /* "m.bunjang.co.kr/products/1", "musinsa.com/app/goods/2": a host with a dot. */
      return /^(www\.)?[\w-]+(\.[\w-]+)+([/?#]|$)/i.test(text);
    }

    function syncAddLabel() {
      var text = els.urlInput.value.trim();
      els.addLabel.textContent = text && !looksLikeUrl(text) ? searchLabelText : addLabelText;
    }

    function addOrSearch() {
      var text = els.urlInput.value.trim();
      if (!text) {
        els.urlInput.focus();
        return;
      }
      if (looksLikeUrl(text)) {
        addFromUrl();
        return;
      }
      showPasteError('');
      window.open(searchUrl + '?q=' + encodeURIComponent('korea-' + text), '_blank', 'noopener');
    }

    els.addBtn.addEventListener('click', addOrSearch);
    els.urlInput.addEventListener('input', syncAddLabel);
    els.urlInput.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        addOrSearch();
      }
    });

    /* HAR-249: "Start request" buttons elsewhere on the page (the CTA band at
       the bottom) bring the shopper back up to this box. */
    document.querySelectorAll('[data-harum-ps-start]').forEach(function (button) {
      if (button.dataset.harumPsStartBound) return;
      button.dataset.harumPsStartBound = '1';
      button.addEventListener('click', function (event) {
        event.preventDefault();
        els.urlInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        els.urlInput.focus({ preventScroll: true });
      });
    });

    /* A clean slate every time (HAR-238): reading the paste field here meant
       the link of a product the shopper had just abandoned came along. */
    els.manualBtn.addEventListener('click', function () {
      openDialog({
        url: '',
        name: '',
        nameOriginal: null,
        price: null,
        quantity: 1,
        comments: '',
        secondHand: false,
        image: null,
        domesticShipping: null,
        priceNote: ''
      });
    });

    els.resumeBtn.addEventListener('click', function () {
      if (!state.stash) return;
      var draft = state.stash;
      setStash(null);
      openDialog(draft);
    });

    els.itemList.addEventListener('click', function (event) {
      var editIndex = event.target.getAttribute('data-edit');
      var removeIndex = event.target.getAttribute('data-remove');

      if (editIndex !== null) {
        var item = state.items[Number(editIndex)];
        openDialog(
          {
            url: item.url,
            name: item.name,
            nameOriginal: item.nameOriginal || null,
            price: item.price,
            quantity: item.quantity,
            comments: item.comments,
            secondHand: item.secondHand,
            image: item.image,
            domesticShipping: item.domesticShipping,
            priceNote: ''
          },
          Number(editIndex)
        );
      } else if (removeIndex !== null) {
        state.items.splice(Number(removeIndex), 1);
        renderItems();
      }
    });

    els.qtyUp.addEventListener('click', function () {
      els.dialogQty.value = (parseInt(els.dialogQty.value, 10) || 0) + 1;
      renderDialogSubtotal();
    });
    els.qtyDown.addEventListener('click', function () {
      els.dialogQty.value = Math.max(1, (parseInt(els.dialogQty.value, 10) || 2) - 1);
      renderDialogSubtotal();
    });
    els.dialogQty.addEventListener('input', renderDialogSubtotal);
    els.dialogPrice.addEventListener('input', renderDialogSubtotal);
    els.dialogSave.addEventListener('click', saveDialog);
    els.dialogClose.addEventListener('click', function () {
      closeOverlay(els.dialog);
    });

    els.country.addEventListener('change', applyCountryRules);
    els.method.addEventListener('change', renderSummary);
    els.weight.addEventListener('input', renderSummary);
    els.taxAgree.addEventListener('change', function () {
      els.submitError.hidden = true;
    });

    els.submit.addEventListener('click', openReview);
    els.reviewBack.addEventListener('click', function () {
      closeOverlay(els.review);
    });
    els.reviewClose.addEventListener('click', function () {
      closeOverlay(els.review);
    });
    els.reviewConfirm.addEventListener('click', submitOrder);
    els.doneClose.addEventListener('click', function () {
      closeOverlay(els.done);
    });

    if (els.cost) {
      root.querySelectorAll('[data-ps-cost-open]').forEach(function (button) {
        button.addEventListener('click', function () {
          openOverlay(els.cost);
        });
      });
      els.costClose.addEventListener('click', function () {
        closeOverlay(els.cost);
      });
    }

    applyCountryRules();
    renderItems();
    renderRateNotes();

    /* HARUM (HAR-235): external search result cards land here with
       ?add=<product url> — start the paste flow with it so the shopper
       arrives straight in the confirm dialog. */
    var addParam = new URLSearchParams(window.location.search).get('add');
    if (addParam && /^https?:\/\//i.test(addParam) && !state.items.length) {
      els.urlInput.value = addParam;
      addFromUrl();
    }
  }

  function boot() {
    document
      .querySelectorAll('[data-harum-personal-shopper]')
      .forEach(function (root) {
        if (root.dataset.harumPsReady) return;
        root.dataset.harumPsReady = '1';
        init(root);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-renders the section without a page load. */
  document.addEventListener('shopify:section:load', boot);
})();
